import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card, Group, Stack, Text, Badge, Button, Box, ScrollArea, Loader, SimpleGrid,
} from '@mantine/core';
import { ChevronRight } from 'lucide-react';

import { gamesApi, walkieApi, type Game, type WalkieStation } from '../api';
import { modeMeta } from './modeMeta';
import { Eyebrow } from './Eyebrow';
import { GroupTag } from './GroupTag';
import { Leaderboard } from './Leaderboard';
import { SituationMap, type MapStation } from './SituationMap';
import { GameQuickSummary } from './GameQuickSummary';
import { numStyle } from '../theme';
import { formatCountdown, formatPoints, plural } from '../utils/format';

function stationLabel(s: { position: number; name: string | null }) {
  return s.name && s.name.trim() ? s.name : `Stanoviště ${s.position}`;
}

function holderOf(s: WalkieStation) {
  if (s.last_holder_group_id == null) return null;
  return s.breakdown.find((b) => b.group_id === s.last_holder_group_id) ?? null;
}

/** Odpočet do dalšího obvolávání — počítá se od začátku posledního kola. */
function useCountdown(startedAt: string | undefined, intervalSeconds: number) {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!startedAt) { setLeft(null); return; }
    const due = new Date(startedAt).getTime() + intervalSeconds * 1000;
    const tick = () => setLeft(due - Date.now());
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startedAt, intervalSeconds]);
  return left;
}

/**
 * Jedna běžící hra na přehledu. U vysílaček ukazuje situační mapu, odpočet kola,
 * průběžné skóre a poslední zápisy; ostatní módy jen hlavičku s odkazem na detail,
 * protože jejich průběh se z přehledu smysluplně shrnout nedá.
 */
export function RunningGameCard({ game }: { game: Game }) {
  const navigate = useNavigate();
  const meta = modeMeta(game.game_type_id);
  const Icon = meta.icon;
  const isWalkie = game.game_type_id === 'walkie';

  const walkie = useQuery({
    queryKey: ['walkieState', game.uuid],
    queryFn: () => walkieApi.getState(game.uuid),
    enabled: isWalkie,
    refetchInterval: 20_000,
  });
  const rounds = useQuery({
    queryKey: ['walkieRounds', game.uuid],
    queryFn: () => gamesApi.getWalkieRounds(game.uuid),
    enabled: isWalkie,
    refetchInterval: 20_000,
  });

  const lastRound = walkie.data?.activeRound ?? walkie.data?.lastCompletedRound ?? null;
  const left = useCountdown(lastRound?.started_at, walkie.data?.settings.round_interval_seconds ?? 600);

  const stations = walkie.data?.stations ?? [];
  const mapStations: MapStation[] = stations.map((s) => {
    const holder = holderOf(s);
    return {
      id: s.id, position: s.position, label: stationLabel(s), points: s.points,
      holderName: holder?.group_name ?? null, holderColor: holder?.group_color ?? null,
    };
  });

  const feed = (rounds.data?.rounds ?? [])
    .flatMap((r) =>
      r.calls.map((c) => ({
        key: `${r.round_number}-${c.station_id}`,
        at: c.recorded_at,
        round: r.round_number,
        station: rounds.data!.stations.find((s) => s.id === c.station_id),
        call: c,
      }))
    )
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .slice(0, 6);

  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      <Group p="sm" gap="sm" wrap="wrap" align="flex-start">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={7}>
            <Badge color="flag" leftSection={<span className="live-dot" />}>právě běží</Badge>
            <Icon size={13} color={`var(--mantine-color-${meta.color}-6)`} />
            <Eyebrow>{meta.label}</Eyebrow>
          </Group>
          <Text fw={700} fz="lg" mt={2} truncate>{game.name}</Text>
          {isWalkie && walkie.data && (
            <Text c="dimmed" fz="xs" mt={2} style={numStyle}>
              {plural(stations.length, 'stanoviště', 'stanoviště', 'stanovišť')} ·
              {' '}kolo každých {Math.round(walkie.data.settings.round_interval_seconds / 60)} min ·
              {' '}{plural(walkie.data.roundsCount, 'kolo', 'kola', 'kol')}
            </Text>
          )}
        </Box>
        {isWalkie && lastRound && left !== null && (
          <Card padding={8} withBorder>
            <Eyebrow>Kolo {lastRound.round_number} · {left > 0 ? 'zbývá' : 'čeká se'}</Eyebrow>
            <Text fw={700} fz={18} style={numStyle} c="flag" lh={1.2}>
              {left > 0 ? formatCountdown(left) : '0:00'}
            </Text>
          </Card>
        )}
        <Button size="xs" variant="default" rightSection={<ChevronRight size={14} />}
          onClick={() => navigate(`/hry/${game.uuid}`)}>
          Detail hry
        </Button>
      </Group>

      {!isWalkie && <GameQuickSummary mode={game.game_type_id} gameUuid={game.uuid} />}

      {isWalkie && walkie.isLoading && (
        <Group justify="center" py="lg"><Loader size="sm" /></Group>
      )}

      {isWalkie && mapStations.length > 0 && (
        <>
          <SituationMap stations={mapStations} height={200} />
          <ScrollArea type="auto" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
            <Group gap={0} wrap="nowrap">
              {stations.map((s) => {
                const holder = holderOf(s);
                return (
                  <Stack key={s.id} gap={3} p="sm"
                    style={{ flex: '1 0 140px', borderRight: '1px solid var(--mantine-color-default-border)' }}>
                    <Group gap={5} wrap="nowrap">
                      <Text c="dimmed" fz={10} style={numStyle}>{s.position}</Text>
                      <Text fw={650} fz="sm" truncate>{stationLabel(s)}</Text>
                      <Text c="dimmed" fz={10} style={numStyle}>{formatPoints(s.points)} b</Text>
                    </Group>
                    {holder
                      ? <GroupTag name={holder.group_name} color={holder.group_color} />
                      : <Text c="dimmed" fz="xs">{s.has_last_call ? 'nikdo se neozval' : 'bez zápisu'}</Text>}
                  </Stack>
                );
              })}
            </Group>
          </ScrollArea>
        </>
      )}

      {isWalkie && ((walkie.data?.leaderboard?.length ?? 0) > 0 || feed.length > 0) && (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing={0}
          style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          {(walkie.data?.leaderboard?.length ?? 0) > 0 && (
            <Box style={{ borderRight: '1px solid var(--mantine-color-default-border)' }}>
              <Group p="sm" pb={4}><Eyebrow>Průběžné skóre</Eyebrow></Group>
              <Leaderboard
                rows={walkie.data!.leaderboard.map((g) => ({
                  key: g.group_id, name: g.group_name, value: g.total_points,
                  color: g.group_color, sub: `${g.calls_won}× se ozvali`,
                }))}
              />
            </Box>
          )}
          {feed.length > 0 && (
            <Box>
              <Group p="sm" pb={4}><Eyebrow>Poslední zápisy</Eyebrow></Group>
              {feed.map((f) => (
                <Group key={f.key} px="md" py={6} gap={9} wrap="nowrap"
                  style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                  <Text c="dimmed" fz={10} style={numStyle} w={40}>
                    {new Date(f.at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                  </Text>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    {f.call.group_id == null ? (
                      <Text fz="sm" c="dimmed">
                        Nikdo se neozval — {f.station ? stationLabel(f.station) : 'stanoviště'}
                      </Text>
                    ) : (
                      <Group gap={6} wrap="nowrap">
                        <GroupTag name={f.call.group_name} color={f.call.group_color} />
                        <Text fz="sm" c="dimmed" truncate>
                          na {f.station ? stationLabel(f.station) : 'stanovišti'}
                        </Text>
                        <Text fz={10} c="dimmed" style={numStyle}>+{formatPoints(f.call.points)}</Text>
                      </Group>
                    )}
                  </Box>
                  <Text c="dimmed" fz={10} style={numStyle}>kolo {f.round}</Text>
                </Group>
              ))}
            </Box>
          )}
        </SimpleGrid>
      )}
    </Card>
  );
}
